import { BarcodeComponents } from '../types/logistics';

// ===========================================
// Barcode Format & Display Utilities
// ===========================================
// Centralizes all barcode parsing, display formatting,
// and matching logic for EAN-13 barcodes.
//
// Barcode structure:
//   12-digit base:   [CCC][OOOO][BBBBB]       (customer, order, boxId)
//   13-digit EAN-13: [CCC][OOOO][BBBBB][X]    (+ check digit)
//
// Display convention: the check digit is part of the highlighted
// "box number" (matches physical EAN-13 labels).

/** Field order for the cross-repo fixture hash. Must match the other two repos. */
export const CANONICAL_FIXTURE_FIELDS = [
  'customerId',
  'orderId',
  'boxId',
  'base12',
  'checkDigit',
  'full13',
  'prefix',
  'boxIdFormatted',
  'displayBoxId',
] as const;

/**
 * EAN-13 check digit for a 12-digit base.
 *
 * Odd positions (1-based) count once, even positions three times. Ported from
 * storage-api's `BarcodeService.calculateCheckSum` and pinned against the
 * shared fixture, which that repo's suite asserts from the other side.
 *
 * This is not barcode generation (Rule 2 of the EAN-13 canon — only the
 * backend generates). `order_boxes.barcode` holds a mix of lengths: 12-digit
 * rows predate the EAN-13 work and are still in service, and `PDFService`
 * appends the check digit to them before printing, so the physical label
 * always carries 13 digits. Recomputing it here reads the label that was
 * already printed rather than inventing one.
 */
export function calculateCheckDigit(base12: string): number {
  let odd = 0;
  let even = 0;
  for (let i = 0; i < base12.length; i += 1) {
    const digit = Number(base12[i]);
    if (Number.isNaN(digit)) continue;
    if (i % 2 === 0) odd += digit;
    else even += digit;
  }
  const total = odd + even * 3;
  return total % 10 === 0 ? 0 : 10 - (total % 10);
}

export interface ParsedBarcode {
  /** The original barcode string */
  raw: string;
  /** Customer + Order digits (dimmed in display) */
  prefix: string;
  /** Box ID digits including check digit (highlighted in display) */
  boxId: string;
  /** Always empty — check digit is now part of boxId */
  check: string;
  /** Whether this is a standard 12 or 13 digit barcode */
  isStandard: boolean;
}

/**
 * Parses a barcode into display components.
 * Uses backend-provided BarcodeComponents when available,
 * falls back to local positional parsing.
 *
 * The check digit is included in boxId (not separate) to match
 * physical EAN-13 labels and avoid confusing couriers/warehouse staff.
 */
export function parseBarcode(
  barcode: string,
  components?: BarcodeComponents | null
): ParsedBarcode {
  // Use backend components if available
  if (components) {
    return {
      raw: barcode,
      prefix: components.base12.slice(0, 7),
      boxId: components.displayBoxId,
      check: '',
      isStandard: true,
    };
  }

  // Local parsing fallback
  const digits = barcode.replace(/\D/g, '');

  if (digits.length === 13) {
    return {
      raw: barcode,
      prefix: digits.slice(0, 7),
      boxId: digits.slice(7),
      check: '',
      isStandard: true,
    };
  }

  if (digits.length === 12) {
    // A 12-digit stored barcode still has a 13-digit printed label —
    // PDFService appends the check digit before rasterising — so the box
    // number shown has to include it, or a courier comparing screen to label
    // sees five digits against six. This is the same value the backend would
    // have sent as `displayBoxId`; the branch only runs when it sent nothing.
    return {
      raw: barcode,
      prefix: digits.slice(0, 7),
      boxId: digits.slice(7) + String(calculateCheckDigit(digits)),
      check: '',
      isStandard: true,
    };
  }

  // Non-standard barcode — best-effort fallback
  return {
    raw: barcode,
    prefix: barcode.slice(0, -4),
    boxId: barcode.slice(-4),
    check: '',
    isStandard: false,
  };
}

/**
 * Returns a short human-readable label for a barcode.
 * Shows the box ID portion (6 digits for 13-digit EAN barcodes,
 * 5 digits for 12-digit, last 4 chars for non-standard).
 * Falls back to box ID number when barcode is null.
 */
export function barcodeShortLabel(
  barcode: string | null | undefined,
  boxId?: number,
  components?: BarcodeComponents | null
): string {
  if (!barcode) {
    return boxId != null ? `#${boxId}` : '#?';
  }
  const parsed = parseBarcode(barcode, components);
  return parsed.boxId;
}

/**
 * Normalizes a barcode to its 12-digit base form
 * by stripping the EAN-13 check digit if present.
 */
export function normalizeToBase12(barcode: string): string {
  const digits = barcode.replace(/\D/g, '');
  if (digits.length === 13) return digits.slice(0, 12);
  return digits;
}

/**
 * Compares two barcodes for equality, normalizing both
 * to base-12 to handle 12/13-digit cross-matching.
 */
export function barcodesMatch(a: string, b: string): boolean {
  if (!a || !b) return false;
  if (a === b) return true;
  return normalizeToBase12(a) === normalizeToBase12(b);
}
